import moment from 'moment-timezone'
import { CartResetType, DoResetProps } from '@/hooks/useReset'
import { HandleDeleteCartAndLogoutFunction } from '@/app/_providers/LoginProvider/LoginContext'
import { cartExists } from '@/utils/cart.utils'
import TXT from '@/messages'

const getCountdownAlertText = (
  time: string,
  measurement: 'seconds' | 'minutes' = 'seconds',
): string => TXT.timer.cartExpiresIn(time, measurement)

/**
 * Get screen reader alert messages
 * @param {number} minutes - minutes remaining
 * @param {number} seconds - seconds remaining
 * @returns {string|null} alert message
 */
export const handleCountdownAlert = (minutes: number, seconds: number): string | null => {
  if (minutes === 8 && seconds === 0) {
    return getCountdownAlertText('8', 'minutes')
  }

  if (minutes === 2 && seconds === 0) {
    return getCountdownAlertText('2', 'minutes')
  }

  if (minutes === 0 && seconds === 10) {
    return getCountdownAlertText('10')
  }

  if (minutes === 0 && seconds === 20) {
    return getCountdownAlertText('20')
  }

  if (minutes === 0 && seconds === 30) {
    return getCountdownAlertText('30')
  }

  return null
}

/**
 * Handle cache reset options
 * @param args
 * @param {string} args.cartId - current cart id
 * @param {(props: DoResetProps) => Promise<void>} args.doReset - fn to reset the cache
 * @param {HandleDeleteCartAndLogoutFunction} args.handleResetCart - callback fn to reset the cart
 * @returns {void}
 */
export const handleStartOver = async ({
  cartId,
  doReset,
  handleDeleteCartAndLogout,
} : {
  cartId: string,
  doReset: (props: DoResetProps) => Promise<void>,
  handleDeleteCartAndLogout: HandleDeleteCartAndLogoutFunction,
}): Promise<void> => {
  if (cartId && !cartExists(cartId)) {
    await doReset({ type: CartResetType.NoCart })
    return
  }

  // delete cart and
  // logout from ticketure only
  await handleDeleteCartAndLogout(false)
  await doReset({ type: CartResetType.Full })
}

/**
 * Get seconds till expiration from the provided expires at date string
 * @param {string | null} expiresAt - date string
 * @param {moment.Moment} now - now moment
 * @returns {number} seconds till expiration
 */
export const getExpiresInFromExpiresAt = (expiresAt: string | undefined, now: moment.Moment): number => (
  expiresAt
    ? moment(expiresAt).diff(now, 'seconds')
    : 0
)
